/**
 * The Details view — docs/06-UI-SPEC.md §11.1.
 *
 * Windows Explorer, which is what the owner asked for: five columns, click a header
 * to sort by it, click it again to turn it round, and an arrow on the column doing
 * the sorting so it is never a guess which one that is.
 */

import {
  COLUMN_LABELS,
  dateLabel,
  durationLabel,
  sizeLabel,
  studentsLabel,
  type Lecture,
  type LectureColumn,
  type SortOrder,
} from '../../lectures.js';
import { StarButton } from './StarButton.js';

const COLUMNS: readonly LectureColumn[] = ['name', 'date', 'duration', 'students', 'size'];

/** The columns that hold numbers sit right, the way a spreadsheet does. */
const NUMERIC: ReadonlySet<LectureColumn> = new Set<LectureColumn>(['duration', 'size']);

export interface DetailsTableProps {
  readonly lectures: readonly Lecture[];
  readonly sort: SortOrder;
  readonly onSort: (column: LectureColumn) => void;
  readonly onOpen: (lecture: Lecture) => void;
  readonly onStar: (lecture: Lecture) => void;
}

function HeaderCell({
  column,
  sort,
  onSort,
}: {
  readonly column: LectureColumn;
  readonly sort: SortOrder;
  readonly onSort: (column: LectureColumn) => void;
}) {
  const active = sort.column === column;
  return (
    <th
      scope="col"
      aria-sort={active ? sort.direction : 'none'}
      className={`border-b border-border p-0 ${NUMERIC.has(column) ? 'text-right' : 'text-left'}`}
    >
      <button
        type="button"
        onClick={() => {
          onSort(column);
        }}
        className="flex min-h-touch w-full items-center gap-2 px-3 text-sm font-semibold text-text-secondary transition-colors duration-fast hover:bg-surface-hover"
      >
        <span>{COLUMN_LABELS[column]}</span>
        <span aria-hidden className="text-text-placeholder">
          {active ? (sort.direction === 'ascending' ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

function Row({
  lecture,
  onOpen,
  onStar,
}: {
  readonly lecture: Lecture;
  readonly onOpen: () => void;
  readonly onStar: () => void;
}) {
  return (
    <tr
      tabIndex={0}
      onDoubleClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen();
      }}
      className="cursor-default border-b border-border text-base text-text-primary transition-colors duration-fast hover:bg-surface-hover focus:bg-surface-hover focus:outline-none"
    >
      <td className="w-touch px-1 py-1">
        <StarButton starred={lecture.isStarred} onToggle={onStar} />
      </td>
      <td className="max-w-[420px] truncate px-3 py-2" title={lecture.filename}>
        {lecture.filename}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-text-secondary">{dateLabel(lecture)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-text-secondary">
        {durationLabel(lecture)}
      </td>
      <td className="max-w-[220px] truncate px-3 py-2 text-text-secondary">
        {studentsLabel(lecture)}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-text-secondary">
        {sizeLabel(lecture)}
      </td>
    </tr>
  );
}

export function DetailsTable({
  lectures,
  sort,
  onSort,
  onOpen,
  onStar,
}: DetailsTableProps) {
  return (
    <div className="w-full overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th scope="col" className="w-touch border-b border-border">
              <span className="sr-only">Starred</span>
            </th>
            {COLUMNS.map((column) => (
              <HeaderCell key={column} column={column} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {lectures.map((lecture) => (
            <Row
              key={lecture.id}
              lecture={lecture}
              onOpen={() => {
                onOpen(lecture);
              }}
              onStar={() => {
                onStar(lecture);
              }}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
